import { type DataTableColumn, type Gutters, type SortState } from './types'
import { TableHead, TableHeadCell } from '@/components/base'
import { SelectionCheckbox } from '@/components/Internal/Selection'

type DataTableHeaderProps<T> = {
  columns: DataTableColumn<T>[]
  sortState?: SortState
  onSortChange?: (next: SortState) => void
  /** The same list the rows lay out, so the two sets of cells share edges. */
  gutters: Gutters
  /** The property each resized column reads its width from, which the rows
   *  read too. */
  columnVars?: Record<string, string>
  onResizeColumn?: (key: string, width: number | null) => void
  selectionState?: boolean | 'indeterminate'
  onToggleSelectAllVisible?: () => void
}

/**
 * @single-use part of `DataTable`, which is what a screen renders
 */
export function DataTableHeader<T>({
  columns,
  sortState,
  onSortChange,
  gutters,
  columnVars,
  onResizeColumn,
  selectionState,
  onToggleSelectAllVisible,
}: DataTableHeaderProps<T>) {
  return (
    <TableHead>
      {/* Only the checkbox has a head; the rest are the spacers the rows
          reserve, without which the cells sit left of theirs. */}
      {gutters.leading.map(({ key, className }) => (
        <div key={key} className={className}>
          {key === 'select' ? (
            <SelectionCheckbox
              checked={selectionState ?? false}
              onToggle={() => onToggleSelectAllVisible?.()}
            />
          ) : null}
        </div>
      ))}

      {columns.map(column => {
        const isActive = sortState?.key === column.key
        const sortable = (column.sortable || column.sortValue) && onSortChange

        return (
          <TableHeadCell
            key={column.key}
            width={column.width}
            widthVar={columnVars?.[column.key]}
            align={column.align}
            onResize={
              onResizeColumn
                ? width => onResizeColumn(column.key, width)
                : undefined
            }
            sort={isActive ? sortState.direction : false}
            onSort={
              sortable
                ? () =>
                    onSortChange({
                      key: column.key,
                      direction:
                        isActive && sortState.direction === 'asc'
                          ? 'desc'
                          : 'asc',
                    })
                : undefined
            }
          >
            {column.label}
          </TableHeadCell>
        )
      })}

      {gutters.trailing.map(({ key, className }) => (
        <div key={key} className={className} />
      ))}
    </TableHead>
  )
}
